import { useTranslation } from 'react-i18next';
import { useMemo } from 'react';

import { getApplicationStatuses } from 'pages/dashboard/selectors';
import { getPermissions } from 'pages/dashboard/selectors';
import Button, { ButtonSize } from 'components/Button';
import { useAppSelector, useAppDispatch } from 'hooks';
import { getTenantPhoneNumber } from 'theme/selectors';
import { getBusiness } from 'pages/Profile/selectors';
import { Permissions } from 'entities/dashboard';
import {
  setShowOatFiOnboardingModal,
  setIsModalOpened,
  setStep,
} from 'components/submitDashboard/submitDashboardSlice';
import {
  ApplicationStatuses,
  FunderOnboardingStatuses,
} from 'entities/progress';
import {
  ExclamationCircleIcon,
  ClipboardCopyIcon,
  CheckCircleIcon,
  CloseCircleIcon,
  ClockMiniIcon,
  DotIcon,
  ProgressIcon,
} from 'components/icons';

import { getProcent } from '../selectors';

type IconComponent = (props: { className?: string }) => JSX.Element;
type StatusIcon = { Icon: IconComponent; className: string };

const DotInCircleIcon: IconComponent = ({ className }) => (
  <div className="w-6 h-6 rounded-full bg-gray-100 flex items-center justify-center">
    <DotIcon className={className} />
  </div>
);

const ICON_BASE_CLASS = 'flex-shrink-0 w-5 h-5';

const APP_STATUS_ICONS: Record<string, StatusIcon> = {
  [ApplicationStatuses.approved]: {
    Icon: CheckCircleIcon,
    className: 'text-green-600',
  },
  [ApplicationStatuses.pendingEnrollment]: {
    Icon: ClipboardCopyIcon,
    className: 'text-yellow-500',
  },
  [ApplicationStatuses.declined]: {
    Icon: ExclamationCircleIcon,
    className: 'text-red-500',
  },
  [ApplicationStatuses.suspended]: {
    Icon: CloseCircleIcon,
    className: 'text-red-500',
  },
};

const APP_STATUS_ICON_FALLBACK: StatusIcon = {
  Icon: ClockMiniIcon,
  className: 'text-blue-600',
};

const FUNDER_STATUS_ICONS: Record<string, StatusIcon> = {
  [FunderOnboardingStatuses.approved]: {
    Icon: CheckCircleIcon,
    className: 'text-green-600',
  },
  [FunderOnboardingStatuses.pendingApproval]: {
    Icon: ClockMiniIcon,
    className: 'text-blue-600',
  },
  [FunderOnboardingStatuses.declined]: {
    Icon: CheckCircleIcon,
    className: 'text-green-600',
  },
  [FunderOnboardingStatuses.pending]: {
    Icon: ExclamationCircleIcon,
    className: 'text-yellow-500',
  },
  [FunderOnboardingStatuses.approvedNotSigned]: {
    Icon: ExclamationCircleIcon,
    className: 'text-yellow-500',
  },
};

const FUNDER_STATUS_ICON_FALLBACK: StatusIcon = {
  Icon: DotInCircleIcon,
  className: 'text-gray-500',
};

const CapitalProductProgress = () => {
  const { t } = useTranslation('dashboard');

  const dispatch = useAppDispatch();

  const statuses = useAppSelector(getApplicationStatuses);
  const appPercent = useAppSelector(getProcent);
  const business = useAppSelector(getBusiness);
  const permissions = useAppSelector(getPermissions);
  const tenantPhoneNumber = useAppSelector(getTenantPhoneNumber);

  const currentStep = useMemo(() => {
    if (business.submittedByPersonsId) return 4;
    if (appPercent === 100) return 2;
    return 1;
  }, [appPercent, business.submittedByPersonsId]);

  const handleApply = () => {
    dispatch(setShowOatFiOnboardingModal(true));
  };

  const handleTerms = () => {
    dispatch(setStep(2));
    dispatch(setIsModalOpened(true));
  };

  const funderOnboardingStatus = (
    business.funderOnboardingStatusName ?? ''
  ).toLowerCase();

  const { Icon: AppStatusIcon, className: appStatusIconColor } =
    APP_STATUS_ICONS[business.applicationStatusTypeCode ?? ''] ??
    APP_STATUS_ICON_FALLBACK;

  const { Icon: FunderStatusIcon, className: funderStatusIconColor } =
    FUNDER_STATUS_ICONS[funderOnboardingStatus] ?? FUNDER_STATUS_ICON_FALLBACK;

  const needsMoreFunderSteps =
    !!business.oatFiBusinessExternalId &&
    (funderOnboardingStatus === FunderOnboardingStatuses.pending ||
      funderOnboardingStatus === FunderOnboardingStatuses.approvedNotSigned);

  const hasSubmitAppPermission = permissions.find(
    (p) => p.permissions_Code === Permissions.submitApp,
  );

  return (
    <div className="py-2 px-2.5 border border-gray-200 bg-gray-50 rounded-lg">
      <div className="py-1 flex flex-col">
        <div className="flex items-start gap-2.5 pt-0.5 pb-2.5">
          {currentStep > 1 ? (
            <CheckCircleIcon className="flex-shrink-0 text-green-600 w-5 h-5" />
          ) : (
            <ProgressIcon className="flex-shrink-0 text-blue-600 w-5 h-5" />
          )}

          <div className="flex-1">
            <p
              className={`text-sm ${
                currentStep === 1
                  ? 'text-blue-600 font-medium'
                  : 'text-gray-700'
              } ${currentStep > 1 ? 'font-medium' : ''}`}
            >
              {t('progress.capital.steps.businessInfoTitle')}
            </p>
            {currentStep === 1 && (
              <p className="mt-1 text-sm text-gray-500">
                {t('progress.capital.steps.businessInfoText')}
              </p>
            )}
          </div>
        </div>

        <div className="flex items-start gap-2.5 py-2.5">
          {currentStep > 2 ? (
            <CheckCircleIcon className="flex-shrink-0 text-green-600 w-5 h-5" />
          ) : currentStep === 2 ? (
            <>
              {hasSubmitAppPermission ? (
                <ProgressIcon className="flex-shrink-0 text-blue-600 w-5 h-5" />
              ) : (
                <ClockMiniIcon className="flex-shrink-0 text-blue-600 w-5 h-5" />
              )}
            </>
          ) : (
            <div className="w-6 h-6 rounded-full bg-gray-100 flex items-center justify-center">
              <DotIcon className="flex-shrink-0 text-gray-500 w-5 h-5" />
            </div>
          )}

          <div className="flex-1">
            <p
              className={`text-sm ${
                currentStep === 2 && hasSubmitAppPermission
                  ? 'text-blue-600'
                  : 'text-gray-700'
              } ${currentStep >= 2 ? 'font-medium' : ''}`}
            >
              {t('progress.capital.steps.acceptanceTitle')}
            </p>

            {currentStep === 2 && (
              <>
                {hasSubmitAppPermission ? (
                  <div>
                    <p className="mt-1 text-sm text-gray-500">
                      {t('progress.capital.steps.acceptanceText')}
                    </p>
                    <Button
                      className="mt-3.5"
                      heightClass="h-8"
                      size={ButtonSize.medium}
                      onClick={handleTerms}
                    >
                      {t('common:continue')}
                    </Button>
                  </div>
                ) : (
                  <p className="text-sm text-gray-500">
                    {t('progress.capital.steps.acceptanceSigned')}
                  </p>
                )}
              </>
            )}
          </div>
        </div>

        <div className="flex items-start gap-2.5 pt-2.5 pb-1">
          {currentStep < 3 ? (
            <div className="w-6 h-6 rounded-full bg-gray-100 flex items-center justify-center">
              <DotIcon className={`${ICON_BASE_CLASS} text-gray-500`} />
            </div>
          ) : (
            <AppStatusIcon
              className={`${ICON_BASE_CLASS} ${appStatusIconColor}`}
            />
          )}

          <div className="flex-1">
            <p
              className={`text-sm text-gray-700 ${
                currentStep >= 3 ? 'font-medium' : ''
              }`}
            >
              {t('progress.capital.steps.submissionTitle')}
            </p>
            {currentStep >= 3 && (
              <p className="text-gray-500 text-sm mt-1">
                {statuses
                  .find(
                    (s) => s.typeCode === business.applicationStatusTypeCode,
                  )
                  ?.description.replace('{{phone}}', tenantPhoneNumber)}
              </p>
            )}
          </div>
        </div>

        <div className="h-px w-full bg-gray-200 my-2.5" />

        <div className="flex items-start gap-2.5 py-0.5">
          {!business.oatFiBusinessExternalId ? (
            <div className="w-6 h-6 rounded-full bg-gray-100 flex items-center justify-center -mt-0.5">
              <DotIcon className={`${ICON_BASE_CLASS} text-gray-500`} />
            </div>
          ) : (
            <FunderStatusIcon
              className={`${ICON_BASE_CLASS} ${funderStatusIconColor}`}
            />
          )}

          <div className="flex-1">
            <p
              className={`text-sm text-gray-700 ${
                currentStep === 4 ? 'font-medium' : ''
              }`}
            >
              {t('progress.capital.steps.locTitle')}
            </p>

            {![
              ApplicationStatuses.declined,
              ApplicationStatuses.suspended,
            ].includes(
              business.applicationStatusTypeCode as ApplicationStatuses,
            ) && (
              <>
                {currentStep === 4 && !business.oatFiBusinessExternalId && (
                  <div>
                    <p className="text-gray-500 text-sm">
                      {t('progress.capital.steps.locApply')}
                    </p>
                    <Button
                      className="mt-3.5"
                      heightClass="h-8"
                      size={ButtonSize.medium}
                      onClick={handleApply}
                    >
                      {t('common:apply')}
                    </Button>
                  </div>
                )}

                {currentStep === 4 && needsMoreFunderSteps && (
                  <div>
                    <p className="text-gray-500 text-sm">
                      {t('progress.capital.steps.locMoreSteps')}
                    </p>
                    <Button
                      className="mt-3.5"
                      heightClass="h-8"
                      size={ButtonSize.medium}
                      onClick={handleApply}
                    >
                      {t('progress.capital.completeLink')}
                    </Button>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CapitalProductProgress;
